import React, { useMemo } from 'react';
import { graphql } from 'gatsby';

import {
    Container,
    Row,
    Col,
    className,
    Text,
    Heading,
    SocialIcons,
} from 'design-system';
import { useTranslate } from 'gatsby-theme-soluble-source';

import TransitionLink from '../TransitionLink';
import NewsletterSection from '../NewsletterSection';

import { getLanguageNavByAlternateLanguages } from '../../utils';

import style from './footer.module.scss';

function orderByPosition(array) {
    return array?.length ? array.sort((a, b) => {
        if (a.position < b.position) {
            return -1;
        }
        if (a.position > b.position) {
            return 1;
        }
        return 0;
    }) : [];
}

export default function Footer({ alternateLanguages = [], layoutProps, data }) {
    const translate = useTranslate();
    const languageNav = useMemo(
        () =>
            getLanguageNavByAlternateLanguages(
                alternateLanguages,
                layoutProps?.specificLanguages,
            ),
        [alternateLanguages, layoutProps?.specificLanguages],
    );

    const [servicesNav, corporateNav, legalNav] = useMemo(() => {
        return [
            data?.links?.nodes?.length ? data.links.nodes[0] : null,
            data?.links?.nodes?.length > 1 ? data.links.nodes[1] : null,
            data?.links?.nodes?.length > 2 ? data.links.nodes[2] : null,
        ];
    }, [data?.links?.nodes]);

    return (
        <>
            {data?.newsletterButton?.newsletterButton &&
                !layoutProps?.hideNewsletterSection ? (
                <NewsletterSection
                    button={data.newsletterButton.newsletterButton}
                />
            ) : null}
            <footer data-datocms-noindex className={style.wrapper}>
                <Container pt={{ xs: 11 }} pb={{ xs: 9 }}>
                    <Row className={layoutProps?.minimal ? '' : 'mb-lg-4'}>
                        <Col col={{ lg: 2 }}>
                            <img
                                alt={`${data.site.siteMetadata.title} logo`}
                                src={data.logoWhite.publicURL}
                                {...className(style.logo)}
                            />
                        </Col>
                        {!layoutProps?.minimal && (
                            <>
                                <Col col={{ lg: 8 }} className={'mt-6 mt-lg-0'}>
                                    <Heading
                                        tag="h6"
                                        className={`subtitle text-uppercase mb-3 mb-lg-4 ${style['footerNavs--title']}`}>
                                        {servicesNav?.label}
                                    </Heading>
                                </Col>
                                <Col
                                    col={{ lg: 2 }}
                                    className={'d-none d-lg-block'}>
                                    <Heading
                                        tag="h6"
                                        className={`subtitle text-uppercase mb-3 mb-lg-4 ${style['footerNavs--title']}`}>
                                        {corporateNav?.label}
                                    </Heading>
                                </Col>
                                {servicesNav
                                    ? orderByPosition(servicesNav.childLinks).map(
                                        (link, index) => {
                                            return (
                                                <Col
                                                    key={index}
                                                    col={{ lg: 2 }}
                                                    offset={{
                                                        lg:
                                                            index === 0
                                                                ? 2
                                                                : 0,
                                                    }}
                                                    className={
                                                        'mb-3 mb-lg-0'
                                                    }>
                                                    <TransitionLink
                                                        {...className(
                                                            `h6 d-block mb-2 ${style['link--title']}`,
                                                        )}
                                                        to={link.href}
                                                        title={link.label}
                                                        target={
                                                            link.openInNewWindow
                                                                ? '_blank'
                                                                : null
                                                        }>
                                                        {link.label}
                                                    </TransitionLink>
                                                    {orderByPosition(link.childLinks).map(
                                                        (sublink, index) => {
                                                            return (
                                                                <TransitionLink
                                                                    key={
                                                                        index
                                                                    }
                                                                    {...className(
                                                                        `body d-block mb-1`,
                                                                    )}
                                                                    to={
                                                                        sublink.href
                                                                    }
                                                                    title={
                                                                        sublink.label
                                                                    }
                                                                    target={
                                                                        link.openInNewWindow
                                                                            ? '_blank'
                                                                            : null
                                                                    }>
                                                                    {
                                                                        sublink.label
                                                                    }
                                                                </TransitionLink>
                                                            );
                                                        },
                                                    )}
                                                </Col>
                                            );
                                        },
                                    )
                                    : null}
                                <Col
                                    col={{ lg: 2 }}
                                    className={
                                        'd-block d-lg-none mt-4 mt-lg-0'
                                    }>
                                    <Heading
                                        tag="h6"
                                        className={`subtitle text-uppercase mb-3 mb-lg-4 ${style['footerNavs--title']}`}>
                                        {corporateNav?.label}
                                    </Heading>
                                </Col>
                                <Col col={{ lg: 2 }}>
                                    {corporateNav
                                        ? orderByPosition(corporateNav.childLinks).map(
                                            (link, index) => {
                                                return (
                                                    <TransitionLink
                                                        key={index}
                                                        {...className(
                                                            `body d-block mb-1`,
                                                        )}
                                                        to={link.href}
                                                        title={link.label}
                                                        target={
                                                            link.openInNewWindow
                                                                ? '_blank'
                                                                : null
                                                        }>
                                                        {link.label}
                                                    </TransitionLink>
                                                );
                                            },
                                        )
                                        : null}
                                </Col>
                            </>
                        )}
                    </Row>
                    <Row>
                        <Col
                            mt={{
                                xs: 8,
                                lg: 0,
                            }}
                            order={{ lg: 2 }}
                            col={{
                                lg: 2,
                            }}
                            className={
                                layoutProps?.minimal ? style.minimalCol : ''
                            }>
                            <div>
                                <Heading
                                    tag="h6"
                                    className={`subtitle text-uppercase mb-3 ${style['footerNavs--title']}`}>
                                    {translate[`footer.col4.title`].text}
                                </Heading>
                                <SocialIcons
                                    padding={5}
                                    wrapperClassName={style.icons}
                                    icons={[
                                        {
                                            kind: 'linkedin',
                                            url: 'https://www.linkedin.com/company/naeglobal/',
                                        },
                                        {
                                            kind: 'twitter',
                                            url: 'https://twitter.com/nae_es',
                                        },
                                    ]}
                                />
                            </div>
                        </Col>
                        <Col
                            mt={{
                                xs: 8,
                                lg: 0,
                            }}
                            offset={{
                                lg: 7,
                                xxl: 8,
                            }}
                            col={{
                                lg: 3,
                                xxl: 2,
                            }}
                            order={{ lg: 1 }}
                            className={
                                layoutProps?.minimal ? style.minimalCol : ''
                            }>
                            <div>
                                <Heading
                                    tag="h6"
                                    className={`subtitle text-uppercase mb-3 ${style['footerNavs--title']}`}>
                                    {translate[`footer.col4b.title`].text}
                                </Heading>
                                <ul
                                    {...className(
                                        `list-inline ${style['list--lang']}`,
                                    )}>
                                    {languageNav?.length ? (
                                        <>
                                            <li>
                                                <TransitionLink
                                                    {...className(
                                                        `callout p-0 ${style['link--lang']}`,
                                                    )}
                                                    to={languageNav[0].href}
                                                    title={
                                                        languageNav[0].label
                                                    }>
                                                    {languageNav[0].label}
                                                </TransitionLink>
                                            </li>
                                            {languageNav[0].childLinks.map(
                                                (cl, i) => (
                                                    <li key={i}>
                                                        <TransitionLink
                                                            {...className(
                                                                `callout p-0 ${style['link--lang']}`,
                                                            )}
                                                            to={cl.href}
                                                            title={cl.label}>
                                                            {cl.label}
                                                        </TransitionLink>
                                                    </li>
                                                ),
                                            )}
                                        </>
                                    ) : null}
                                </ul>
                            </div>
                        </Col>
                    </Row>
                </Container>
                <div {...className(style['footer-bottom'])}>
                    <Container pt={{ xs: 5, lg: 3 }} pb={{ xs: 5, lg: 3 }}>
                        <Row>
                            <Col className="text-lg-right">
                                {orderByPosition(legalNav?.childLinks).map((link, i) => (
                                    <TransitionLink
                                        key={i}
                                        {...className(
                                            `subtitle mb-3 mb-lg-0 ml-lg-5 d-block d-lg-inline-block ${style['link--bottom']}`,
                                        )}
                                        to={link.href}
                                        title={link.label}
                                        target={
                                            link.openInNewWindow
                                                ? '_blank'
                                                : null
                                        }>
                                        {link.label}
                                    </TransitionLink>
                                ))}
                                <Text
                                    tag="span"
                                    className={`subtitle ml-lg-5 d-block d-lg-inline-block ${style['copyright']}`}>
                                    {translate['footer.copyright'].text}
                                </Text>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </footer>
        </>
    );
}

export const query = graphql`
    fragment FooterLink on DatoCmsFooter {
        label
        href
        openInNewWindow
    }
    query ($language: String) {
        site {
            siteMetadata {
                title
            }
        }
        links: allDatoCmsFooter(
            filter: {
                treeParent: { id: { eq: null } }
                locale: { eq: $language }
            }
            sort: { position: ASC }
        ) {
            nodes {
                ...FooterLink
                childLinks: treeChildren {
                    ...FooterLink
                    position
                    childLinks: treeChildren {
                        position
                        ...FooterLink
                    }
                }
            }
        }
        newsletterButton: datoCmsDefault(locale: { eq: $language }) {
            newsletterButton {
                ...Button
            }
        }
        logoWhite: file(
            sourceInstanceName: { eq: "img-nae" }
            name: { eq: "logo-white" }
        ) {
            publicURL
        }
    }
`;
